import { useNavigate } from '@tanstack/react-router';
import { Box, Heading, Text, PageHeader } from '@chatsdet/ui';
import {
  Buildings,
  UsersThree,
  Briefcase,
  Pulse,
  Flag,
  GitBranch,
  ChartLine,
  Sliders,
  House,
} from '@phosphor-icons/react';
import { useSuperadminStatsQuery } from './useSuperadminData';

const QUICK_LINKS = [
  { label: 'Organizations', to: '/superadmin/organizations', icon: Buildings, description: 'Manage all organizations' },
  { label: 'Users', to: '/superadmin/users', icon: UsersThree, description: 'User accounts and access' },
  { label: 'Jobs', to: '/superadmin/jobs', icon: Briefcase, description: 'Job orchestration and monitoring' },
  { label: 'Agent Runs', to: '/superadmin/agent-runs', icon: Pulse, description: 'AI agent execution logs' },
  { label: 'Workflows', to: '/superadmin/workflows', icon: GitBranch, description: 'Workflow configuration' },
  { label: 'Feature Flags', to: '/superadmin/feature-flags', icon: Flag, description: 'Feature gating per org' },
  { label: 'Metrics', to: '/superadmin/metrics', icon: ChartLine, description: 'Operational metrics and alarms' },
  { label: 'Cache Telemetry', to: '/superadmin/cache-telemetry', icon: ChartLine, description: 'Workflow cache efficacy charts' },
  { label: 'Knobs', to: '/superadmin/knobs', icon: Sliders, description: 'Global runtime settings' },
] as const;

export function SuperadminDashboard() {
  const navigate = useNavigate();
  const statsQuery = useSuperadminStatsQuery();
  const stats = statsQuery.data?.stats;

  const description = statsQuery.isLoading
    ? 'Loading...'
    : stats
      ? `${stats.totalOrganizations} organizations · ${stats.totalUsers} active users`
      : 'System-wide administration, job orchestration, and observability';

  return (
    <Box className="space-y-8">
      <PageHeader
        icon={<House size={24} />}
        title="Superadmin"
        description={description}
      />

      <Box>
        <Heading level={3} className="mb-4">Quick Access</Heading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK_LINKS.map((link) => (
            <button
              key={link.to}
              onClick={() => navigate({ to: link.to })}
              className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors text-left cursor-pointer"
            >
              <div className="p-2 bg-primary/10 rounded-md shrink-0">
                <link.icon size={18} className="text-primary" />
              </div>
              <div className="min-w-0">
                <Text weight="medium" size="sm">{link.label}</Text>
                <Text size="xs" className="text-muted-foreground mt-0.5">
                  {link.description}
                </Text>
              </div>
            </button>
          ))}
        </div>
      </Box>
    </Box>
  );
}
